// Adaptiv musik uden filer (DESIGN.md: "Lyd"). En glad pentatonisk loop, hvor hver by med strøm
// lægger sin egen tone til, så nettet bogstaveligt talt spiller rigere, jo større det bliver.
// Spændingslag følger stressen: rolig marimba først, så bas, så trommer. Når stormen er drevet
// over, falder de til ro igen. Planlægningen kigger 0,25 s frem og drives af spillets frame-loop.

import type { Mixer } from './mixer';
import { penta } from './sfx';

const BPM = 100;
const STEP = 60 / BPM / 2; // ottendedele
const LOOP = 32;
const LOOKAHEAD = 0.25;

/** Grundtoner pr. takt (fire takter à otte trin): C, A, F, G. */
const ROOTS = [130.81, 110.0, 87.31, 98.0];
/** Marimbaens figur: trin i penta-skalaen pr. ottendedel (-1 = pause). */
const MARIMBA = [0, -1, 2, -1, 4, -1, 2, 3, 0, -1, 2, -1, 5, -1, 4, 2];

export class Music {
  /** Byer med strøm lige nu: hver af dem er én tone i loopet. */
  cities = 0;
  /** 0..1: hvor presset spilleren er. Udglattet i update. */
  tension = 0;
  finale = false;
  private level = 0;
  private playing = false;
  private next = 0;
  private step = 0;
  private resolved = false;

  constructor(private mix: Mixer) {}

  start(): void {
    if (this.playing) return;
    this.playing = true;
    this.resolved = false;
    this.next = 0;
  }

  stop(): void {
    this.playing = false;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  /** Sejr: musikken samles i én akkord og hviler. */
  resolve(): void {
    if (!this.playing || this.resolved) return;
    this.resolved = true;
    this.playing = false;
    const m = this.mix;
    if (!m.live) return;
    const t = m.now + 0.05;
    [261.63, 329.63, 392.0, 523.25, 659.25].forEach((f, i) => {
      m.tone('music', t + i * 0.06, 'sine', f, f, 2.6, 0.08, 0.04);
      m.tone('music', t + i * 0.06, 'triangle', f / 2, f / 2, 2.2, 0.03, 0.06);
    });
  }

  update(dt: number): void {
    // Spændingen stiger hurtigt og falder langsomt, så roen føles fortjent.
    const k = this.tension > this.level ? 1 - Math.exp(-dt / 0.6) : 1 - Math.exp(-dt / 4);
    this.level += (this.tension - this.level) * k;
    const m = this.mix;
    if (!this.playing || !m.live) {
      this.next = 0;
      return;
    }
    const now = m.now;
    if (this.next < now) this.next = now + 0.05;
    while (this.next < now + LOOKAHEAD) {
      this.play(this.step, this.next);
      this.step = (this.step + 1) % LOOP;
      this.next += STEP;
    }
  }

  private play(step: number, t: number): void {
    const m = this.mix;
    const bar = Math.floor(step / 8);
    const root = ROOTS[bar] ?? 130.81;
    const lvl = this.finale ? Math.max(0.8, this.level) : this.level;
    const swing = step % 2 === 1 ? STEP * 0.08 : 0;
    const tt = t + swing;

    // Blød pude på taktens første slag: altid med, så stilheden aldrig er helt tom.
    if (step % 8 === 0) {
      m.tone('music', tt, 'sine', root * 2, root * 2, STEP * 7.5, 0.035, 0.4);
      m.tone('music', tt, 'sine', root * 3, root * 3, STEP * 7, 0.018, 0.5);
    }

    // Marimba: en rolig figur, som altid spiller.
    const mi = MARIMBA[step % MARIMBA.length] ?? -1;
    if (mi >= 0) {
      const f = penta(mi + 5);
      m.tone('music', tt, 'sine', f, f, 0.35, 0.05, 0.004);
      m.tone('music', tt, 'triangle', f * 4, f * 4, 0.05, 0.008, 0.002);
    }

    // Hver by med strøm har sin egen tone på sit eget trin. Flere byer, rigere melodi.
    const n = Math.min(16, this.cities);
    for (let i = 0; i < n; i++) {
      const at = (i * 5 + 3) % 16;
      if (step % 16 !== at) continue;
      const f = penta(((i * 3) % 8) + 7);
      m.tone('music', tt, 'sine', f, f, 0.5, 0.04, 0.005);
      m.tone('music', tt, 'sine', f * 2.01, f * 2.01, 0.15, 0.01, 0.003);
    }

    // Bas: når presset stiger, pulserer grundtonen på hvert andet trin.
    if (lvl > 0.35 && step % 2 === 0) {
      const v = Math.min(1, (lvl - 0.35) / 0.3) * 0.09;
      const f = step % 8 === 6 ? root * 1.5 : root;
      m.tone('music', tt, 'triangle', f, f * 0.98, STEP * 0.9, v, 0.006);
    }

    // Trommer: kun når det virkelig gælder (storm lige over, ure der fylder, finalen).
    if (lvl > 0.62) {
      const v = Math.min(1, (lvl - 0.62) / 0.25);
      if (step % 4 === 0) m.tone('music', tt, 'sine', 120, 45, 0.18, 0.22 * v, 0.002);
      if (step % 8 === 4) m.noise('music', tt, 0.12, 1800, 0.09 * v, 'bandpass', 0.002, 0.7);
      if (step % 2 === 1) m.noise('music', tt, 0.04, 7000, 0.035 * v, 'highpass', 0.001);
    }
  }
}
